"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useContractReads, useNetwork } from "wagmi";
import { fetchToken, FetchTokenResult } from "wagmi/actions";

import { W3_CACHE_TIME, W3_STALE_TIME } from "constants/common";
import { useRx } from "contexts/rx";
import { usePushSubject } from "hooks/rx";
import { useChainsIfNotConnected } from "hooks/util";

import {
  baseTokenSelector,
  quoteTokenSelector,
} from "../selectors/marginly-pool";
import { PoolConfig } from "../types";

type ContractReadConfig =
  | ReturnType<typeof baseTokenSelector>
  | ReturnType<typeof quoteTokenSelector>;

export const useContracts = (
  poolConfig: {
    address: `0x${string}`;
    chainId?: number;
    uniswapAddress?: `0x${string}`;
  }[],
) => {
  const { error$ } = useRx();
  const chains = useChainsIfNotConnected();
  const { chain } = useNetwork();
  const chainId = (chain ?? chains?.[0])?.id;

  const [tokenData, setTokenData] = useState<
    Record<FetchTokenResult["address"], FetchTokenResult>
  >({});

  const currentPools = useMemo(
    () => poolConfig.filter((pool) => chainId === pool.chainId),
    [chainId, poolConfig],
  );

  const contracts = useMemo(
    () =>
      currentPools.reduce<ContractReadConfig[]>((prev, { address }) => {
        return [
          ...prev,
          baseTokenSelector(address),
          quoteTokenSelector(address),
        ];
      }, []),
    [currentPools],
  );

  const {
    data,
    isFetching: pending,
    isSuccess: ready,
    error,
  } = useContractReads({
    contracts,
    watch: true,
    cacheTime: W3_CACHE_TIME,
    staleTime: W3_STALE_TIME,
  });

  usePushSubject(error$, error);

  const { pools, addressToIndex } = useMemo(() => {
    const addressToIndex: Record<`0x${string}`, number> = {};

    const pools = currentPools.map(
      ({ address, uniswapAddress, chainId }, i) => {
        addressToIndex[address] = i;
        const j = i * 2;

        return {
          chainId,
          pool: address,
          baseToken: data?.[j]?.result,
          quoteToken: data?.[j + 1]?.result,
          exchangeContract: uniswapAddress,
        } as PoolConfig;
      },
    );

    return { addressToIndex, pools };
  }, [data, currentPools]);

  const tokens = useMemo(() => {
    const set = new Set<`0x${string}`>();

    for (const { baseToken, quoteToken } of pools ?? []) {
      if (baseToken) {
        set.add(baseToken);
      }

      if (quoteToken) {
        set.add(quoteToken);
      }
    }

    return Array.from(set);
  }, [pools]);

  useEffect(() => {
    Promise.all(tokens.map((address) => fetchToken({ address }))).then(
      (res) => {
        const nextTokenData = res.reduce(
          (acc, el) => ({ ...acc, [el.address]: el }),
          {},
        );
        setTokenData(nextTokenData);
      },
    );
  }, [tokens]);

  const getTokensByPool = useCallback(
    (address?: `0x${string}`) => {
      if (!address || !Number.isFinite(addressToIndex[address])) {
        return undefined;
      }

      const { baseToken, quoteToken, chainId } =
        pools[addressToIndex[address]] ?? {};
      return { baseToken, quoteToken, chainId };
    },
    [addressToIndex, pools],
  );

  return {
    pools,
    pending,
    ready,
    getTokensByPool,
    tokens,
    tokenData,
  };
};
